import { environment } from 'src/environments/environment';
import { isUsableWebsiteSlug } from './website-slug.utils';
import {
  canonicalizeWebsitePlanCode,
  patchWebsiteClaim,
  type WebsiteClaimDraft,
} from './website-claim.storage';

export const WEBSITE_LOGIN_PATH = '/authentication/login';
export const WEBSITE_REGISTER_PATH = '/authentication/register';

/** Plan por defecto en CTAs genéricos (Gratis / Empezar gratis). */
export const WEBSITE_DEFAULT_SIGNUP_PLAN_CODE = 'link' as const;

/**
 * Auth vive en `environment.appOrigin` (prod: milinco.app).
 * En marketing (milinco.com) devolvemos URL absoluta; en el mismo origen o local, relativa.
 * `appOriginOverride` solo para tests.
 */
export function resolveWebsiteAuthHref(pathWithQuery: string, appOriginOverride?: string): string {
  const path = pathWithQuery.startsWith('/') ? pathWithQuery : `/${pathWithQuery}`;
  const appOrigin = (appOriginOverride ?? environment.appOrigin ?? '').replace(/\/$/, '');
  if (!appOrigin) {
    return path;
  }
  const currentOrigin = globalThis.location?.origin ?? '';
  if (currentOrigin && currentOrigin === appOrigin) {
    return path;
  }
  return `${appOrigin}${path}`;
}

export function websiteLoginHref(): string {
  return resolveWebsiteAuthHref(WEBSITE_LOGIN_PATH);
}

export function websiteRegisterHref(claim?: Pick<WebsiteClaimDraft, 'name' | 'slug' | 'planCode'> | null): string {
  const params = new URLSearchParams();
  const name = claim?.name?.trim() ?? '';
  const slug = claim?.slug?.trim() ?? '';
  // Sin plan explícito → Free (Link). Evita wizard con catálogo por defecto.
  const plan = canonicalizeWebsitePlanCode(claim?.planCode) || WEBSITE_DEFAULT_SIGNUP_PLAN_CODE;
  if (name) {
    params.set('name', name);
  }
  if (slug && isUsableWebsiteSlug(slug)) {
    params.set('slug', slug);
  }
  params.set('plan', plan);
  const query = params.toString();
  return resolveWebsiteAuthHref(`${WEBSITE_REGISTER_PATH}?${query}`);
}

/** Guarda el plan en el claim y arma la URL de registro (wizard de crear cuenta). */
export function websiteSignupHrefForPlan(
  planCode: string,
  extras?: Partial<Pick<WebsiteClaimDraft, 'name' | 'slug' | 'bio' | 'logoDataUrl'>>
): string {
  const code = canonicalizeWebsitePlanCode(planCode) || WEBSITE_DEFAULT_SIGNUP_PLAN_CODE;
  const claim = patchWebsiteClaim({
    ...(extras ?? {}),
    planCode: code,
  });
  return websiteRegisterHref(claim);
}

/** CTA genéricos “Gratis / Empezar gratis”: siempre plan Link. */
export function websiteSignupHrefForFree(
  extras?: Partial<Pick<WebsiteClaimDraft, 'name' | 'slug' | 'bio' | 'logoDataUrl'>>
): string {
  return websiteSignupHrefForPlan(WEBSITE_DEFAULT_SIGNUP_PLAN_CODE, extras);
}
